import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getMetadata } from '../../services/chatApi';
import { Download, Upload, Info, FileText, Loader2, Search } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Link, useLocation } from 'react-router-dom';
import { dataURLtoFile } from '../../utils/imageUtils';
import ToolNavigator from '../../components/ToolNavigator';
import useImageTransfer from '../../hooks/useImageTransfer'; 

const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €";

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};


const DisplayMetadata = () => {
    const { t } = useTranslation();
    const { user, setCredits } = useAuth();
    const location = useLocation();
    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [metadata, setMetadata] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const [base64ForNav, setBase64ForNav] = useState(null);
    const resetParams = useCallback(() => {
        setMetadata(null);
        setError(null);
        setBase64ForNav(null);
    }, []);

    useImageTransfer(setFile, setPreviewUrl, setBase64ForNav, resetParams);

    // Handle incoming navigation
    useEffect(() => {
        if (file && !metadata && !isLoading) {
            processMetadata(file);
            
            const reader = new FileReader();
            reader.onloadend = () => {
                setBase64ForNav(reader.result);
            };
            reader.readAsDataURL(file);
        }
    }, [file]);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setPreviewUrl(URL.createObjectURL(selectedFile));
            resetParams();
            
        }
    };

    const processMetadata = async (imageFile) => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await getMetadata(imageFile);
            setMetadata(result.metadata);
	    if (result.new_credits !== undefined) {
                setCredits(result.new_credits);
            }
        } catch (err) {
            setError(t('displayMetadata.errors.generic'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownloadTxt = () => {
        if (!metadata) return;
        
        // Create text content from JSON object
        const textContent = Object.entries(metadata)
            .map(([key, value]) => `${key}: ${value}`)
            .join('\n');
            
        const blob = new Blob([textContent], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = url;
        link.download = "image_metadata.txt";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const resetTool = () => {
        setFile(null);
        setPreviewUrl(null);
        setMetadata(null);
        setBase64ForNav(null);
    };

    
    const hasMetadata = metadata && Object.keys(metadata).length > 0;

    return (
        <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
        {(user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2))? (
                <SubscribePrompt />
            ) : (
            <div className="max-w-6xl mx-auto">
                <h3 className="text-3xl font-bold text-brand-orange mb-2">
                    {t('displayMetadata.title')}
                </h3>
                <p className="text-slate-600 mb-8 max-w-3xl">
                    {t('displayMetadata.description')}
                </p>

                {!file ? (
                    <div className="border-2 border-dashed border-slate-300 rounded-lg p-16 text-center hover:border-brand-orange transition-colors bg-slate-50">
                        <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" id="metadata-upload" />
                        <label htmlFor="metadata-upload" className="cursor-pointer flex flex-col items-center">
                            <div className="bg-white p-4 rounded-full shadow-sm mb-4">
                                <Search size={32} className="text-brand-orange" />
                            </div>
                            <span className="text-xl font-medium text-slate-700 mb-2">{t('displayMetadata.upload.label')}</span>
                            <span className="text-sm text-slate-500">{t('displayMetadata.upload.hint')}</span>
                        </label>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                        
                        {/* Left Column: Image */}
                        <div className="lg:col-span-5">
                            <div className="bg-slate-100 rounded-lg overflow-hidden border border-slate-200 p-4">
                                <h4 className="font-semibold mb-3 flex items-center gap-2">
                                    <Upload size={16} /> {t('displayMetadata.labels.preview')}
                                </h4>
                                <img src={previewUrl} alt="Preview" className="w-full h-auto rounded shadow-sm" />
                                
                                <button 
                                    onClick={resetTool} 
                                    className="mt-4 w-full py-2 px-4 bg-white border border-slate-300 rounded-md text-slate-700 hover:bg-slate-50 transition text-sm"
                                >
                                    {t('displayMetadata.buttons.newImage')}
                                </button>
                            </div>

                            {/* Optional: Allow processing this image with other tools */}
                            {/* We pass previewUrl since we haven't modified the image */}
                            <>
                            {base64ForNav && (
                                <div className="mt-4">
                                    <ToolNavigator currentImage={base64ForNav} /> 
                                </div>
                            )}
                            </>
                        </div>

                        {/* Right Column: Metadata Table */}
                        <div className="lg:col-span-7">
                            <div className="bg-white rounded-lg border border-slate-200 shadow-sm h-full flex flex-col">
                                <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                                    <h4 className="font-semibold flex items-center gap-2 text-brand-orange">
                                        <Info size={18} /> {t('displayMetadata.labels.dataHeader')}
                                    </h4>
                                    {hasMetadata && (
                                        <button 
                                            onClick={handleDownloadTxt}
                                            className="text-sm flex items-center gap-1 text-blue-600 hover:text-blue-800"
                                        >
                                            <Download size={14} /> {t('displayMetadata.buttons.download')}
                                        </button>
                                    )}
                                </div>

                                <div className="p-0 flex-1 overflow-auto max-h-[600px]">
                                    {isLoading ? (
                                        <div className="flex flex-col items-center justify-center h-64 text-slate-400">
                                            <Loader2 className="animate-spin mb-2" size={32} />
                                            <p>{t('displayMetadata.status.analyzing')}</p>
                                        </div>
                                    ) : hasMetadata ? (
                                        <table className="w-full text-sm text-left">
                                            <thead className="text-xs text-slate-500 uppercase bg-slate-50 sticky top-0">
                                                <tr>
                                                    <th className="px-6 py-3 w-1/3">{t('displayMetadata.table.key')}</th>
                                                    <th className="px-6 py-3">{t('displayMetadata.table.value')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {Object.entries(metadata).map(([key, value], index) => (
                                                    <tr key={key} className={`border-b border-slate-100 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                                                        <td className="px-6 py-3 font-medium text-slate-700 break-all">{key}</td>
                                                        <td className="px-6 py-3 text-slate-600 break-all">{value}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <div className="flex flex-col items-center justify-center h-64 text-slate-400">
                                            <FileText size={48} className="mb-2 opacity-50" />
                                            <p>{t('displayMetadata.status.noData')}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                    </div>
                )}
            </div>
            )}
        </div>
    );
};

export default DisplayMetadata;
